import { html } from "core/Controls/Slider.html";
import { CollectionView, View, ViewData, EventListener, Event as MyonEvent } from "silver";

interface SliderEvent<V extends ValueOrPair<number>, S extends ValueOrPair<string>> extends MyonEvent {
	value: V;
	label: S;
}

interface MarksView extends View<ViewData> {
	contents: HTMLDivElement;
}

interface SliderViewData extends ViewData {
	showSpan: boolean;
	isRange: boolean;
	fillLeft: boolean;
	fillRight: boolean;
	label: undefined|string;
	labelledby: undefined | string;
	min: number;
	max: number;
	name: string;
	step: undefined|number;
}

interface LabelViewData extends ViewData {
	label: string;
	percent: number;
}

interface SliderView extends View<SliderViewData> {
	channel: HTMLDivElement;
	span: HTMLDivElement;
	markContainer: HTMLDivElement;
	range: HTMLInputElement;
	rangeB: HTMLInputElement;

	marks: CollectionView<ViewData, MarksView>;
	labels: CollectionView<LabelViewData>;
}

type Pair<V> = [V, V];
type ValueOrPair<V> = V|Pair<V>;

function percentToString(value: number): string {
	return (Math.round(value * 10000) / 100) + "%";
}

export class Slider<V extends ValueOrPair<number>, S extends ValueOrPair<string>> {

	public readonly onSelect = EventListener.create<SliderEvent<V, S>>("select");
	public readonly onDrag = EventListener.create<SliderEvent<V, S>>("select");

	private view: SliderView;
	private formatter: (value: number) => string = String;
	private ariaFormatter?: (value: number) => string;
	private markId: number = 0;

	constructor(
		name: string,
		value: V,
		min: number|string,
		max: number|string
	) {
		const isRange = Array.isArray(value);
		this.view = new View<SliderViewData>(html, {
			formatPercent: percentToString,
			handleChange:  () => this.handleChange(),
			handleInput:   () => this.handleInput(),
			handleKeyup:   () => {
				this.handleInput();
				this.handleChange();
			}
		}, {
			isRange,
			showSpan:   isRange,
			fillLeft:   false,
			fillRight:  false,
			name:       Array.isArray(value) ? `${name}[]` : name,
			min:        parseInt(String(min), 10),
			max:        parseInt(String(max), 10),
			label:      undefined,
			step:       undefined,
			labelledby: undefined
		}) as SliderView;

		this.value = value;
		this.defaultValue = value;
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	public set labelledby(value: string|undefined) {
		this.view.dataset.labelledby = value;
	}

	public get labelledby(): string|undefined {
		return this.view.dataset.labelledby;
	}

	public set label(value: string|undefined) {
		this.view.dataset.label = value;
	}

	public get label(): string|undefined {
		return this.view.dataset.label;
	}

	public set fillMode(value: undefined|"left"|"right") {
		const isRange = this.isRange;
		const fillLeft = value === "left";
		const fillRight = value === "right";
		const showSpan = (isRange || fillLeft || fillRight);
		this.view.set({ fillLeft, fillRight, showSpan });
	}

	private get isRange(): boolean {
		return this.view.dataset.isRange;
	}

	public get min(): number {
		return this.view.dataset.min;
	}

	public set min(value: number) {
		this.view.dataset.min = value;
	}

	public get max(): number {
		return this.view.dataset.max;
	}

	public set max(value: number) {
		this.view.dataset.max = value;
	}

	public static fromRange(input: HTMLInputElement): Slider<number, string> {
		const min = parseInt(input.getAttribute("min") || "", 10);
		const max = parseInt(input.getAttribute("max") || "", 10);
		const value = parseInt(input.getAttribute("value") || "", 10);
		const name = input.getAttribute("name") || "";

		const slider = new Slider<number, string>(name, value, min, max);
		if (input.hasAttribute("step")) {
			slider.setStep(parseInt(input.getAttribute("step") || "", 10));
		}

		const parentNode = input.parentNode;
		if (!parentNode) {
			throw new Error("No parent node");
		}

		parentNode.insertBefore(slider.element, input);
		parentNode.removeChild(input);

		return slider;
	}

	public setDarkMode(value: boolean = true): void {
		this.view.channel.classList.toggle("-dark", value);
	}

	public setTall(value: boolean = true): void {
		this.view.channel.classList.toggle("-tall", value);
	}

	public addMark(title: string, value: number, className: string): this {
		const percent = percentToString(this.valueToPercent(this.round(value)));
		const mark = this.view.marks.add(String(this.markId++), { });

		this.view.markContainer.classList.remove("-empty");
		mark.element.classList.add(className);
		mark.contents.innerHTML = title;
		mark.element.style.left = percent;

		if (!this.isRange) {
			mark.element.addEventListener("click", () => {
				this.value = (value as V);
			});
		}
		return this;
	}

	public setStep(step?: number): this {
		this.view.set({ step });
		return this;
	}

	public setFormatter(formatterFn: (value: number) => string): this {
		this.formatter = formatterFn;
		return this;
	}

	public setAriaFormatter(ariaFormatter: (value: number) => string): this {
		this.ariaFormatter = ariaFormatter;
		this.updateRangeLabel();

		return this;
	}

	public refresh(): void {
		// eslint-disable-next-line
		this.value = this.value;
	}

	public reset(): void {
		this.value = this.defaultValue;
	}

	public set defaultValue(defaultValue: V) {
		if (this.isRange) {
			this.view.range.defaultValue = String((defaultValue as Pair<number>)[0]);
			this.view.rangeB.defaultValue = String((defaultValue as Pair<number>)[1]);
		} else {
			this.view.range.defaultValue = String(defaultValue as number);
		}
	}

	public get defaultValue(): V {
		const value = Number(this.view.range.defaultValue);
		if (!this.isRange) {
			return value as V;
		}
		const valueB = Number(this.view.rangeB.defaultValue);
		return (valueB > value ? [ value, valueB ] : [ valueB, value ]) as V;
	}

	public set value(value: V) {
		if (this.isRange) {
			this.moveRange((value as Pair<number>)[0], this.view.range);
			this.moveRange((value as Pair<number>)[1], this.view.rangeB, true);
		} else {
			this.moveRange(value as number, this.view.range, true);
		}

		this.handleInput();
		this.handleChange();
	}

	public get value(): V {
		const value = Number(this.view.range.value);
		if (!this.isRange) {
			return value as V;
		}
		const valueB = Number(this.view.rangeB.value);
		return (valueB > value ? [ value, valueB ] : [ valueB, value ]) as V;
	}

	public getValueFormatted(): S {
		const value: V = this.value;
		if (this.isRange) {
			return [this.formatter((value as Pair<number>)[0]), this.formatter((value as Pair<number>)[1])] as S;
		}
		return this.formatter(value as number) as S;
	}

	public showNLabels(n: number): Slider<V, S> {
		const step = this.view.dataset.step;
		this.view.labels.empty();
		for (let i = 0; i < n; i++) {
			const percent = i / (n - 1);
			let value = this.percentToValue(percent);

			if (step) {
				value = Math.round(value / step) * step;
			}
			this.view.labels.add(String(i), {
				label: this.formatter(value),
				percent
			});
		}
		return this;
	}

	private updateRangeLabel(): void {
		if (!this.ariaFormatter) {
			return;
		}
		this.view.range.setAttribute("aria-valuetext", this.ariaFormatter(Number(this.view.range.value)));
		if (this.isRange) {
			this.view.rangeB.setAttribute("aria-valuetext", this.ariaFormatter(Number(this.view.rangeB.value)));
		}
	}

	private moveRange(value: number, input: HTMLInputElement, force: boolean = false): void {
		if (value == Number(input.value) && !force) {
			return;
		}
		// value = this.round(value);
		input.value = String(value);

		this.handleInput();
	}

	private updateSpan(): void {
		if (this.isRange) {
			const value = this.value as Pair<number>;
			this.view.span.style.left = percentToString(this.valueToPercent(value[0]));
			this.view.span.style.right = percentToString(1 - this.valueToPercent(value[1]));
		} else {
			const value = this.value as number;
			this.view.span.style.left = percentToString(this.valueToPercent(value));
			this.view.span.style.right = percentToString(1 - this.valueToPercent(value));
		}
	}

	private handleInput(): void {
		this.updateSpan();
		this.updateRangeLabel();
		this.onDrag.trigger({
			value: this.value,
			label: this.getValueFormatted()
		});
	}

	private handleChange(): void {
		this.updateInputOrder();

		this.onSelect.trigger({
			value: this.value,
			label: this.getValueFormatted()
		});
	}

	private updateInputOrder(): void {
		if (!this.isRange) {
			return;
		}
		const valueA = Number(this.view.range.value);
		const valueB = Number(this.view.rangeB.value);
		const focusedElement = document.activeElement;
		if (valueA < valueB && this.view.rangeB.nextElementSibling == this.view.range) {
			(this.view.range.parentNode as HTMLElement).insertBefore(this.view.range, this.view.rangeB);
		} else if (valueB < valueA && this.view.range.nextElementSibling == this.view.rangeB) {
			(this.view.rangeB.parentNode as HTMLElement).insertBefore(this.view.rangeB, this.view.range);
		}
		if (focusedElement instanceof HTMLElement) {
			focusedElement.focus();
		}
	}

	private round(value: number): number {
		const step = this.view.dataset.step;
		if (step) {
			return Math.round(value / step) * step;
		}
		return value;
	}

	private percentToValue(p: number): number {
		return (p * (this.max - this.min)) + this.min;
	}

	private valueToPercent(v: number): number {
		return (v - this.min) / (this.max - this.min);
	}
}
